import { useEffect, useState } from "react";
import { useData } from "../contexts/DataContext";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Search, Loader2, Trash2, Edit, DollarSign } from "lucide-react";

const PRICING_TYPES = [
  { value: "per_unit", label: "Per Unit", description: "Price per unit (EA, BDL, RL, etc.)" },
  { value: "per_square", label: "Per Square", description: "Price per square (100 sq ft)" },
  { value: "per_linear_foot", label: "Per Linear Foot", description: "Price per linear foot" },
  { value: "per_item", label: "Per Item", description: "Fixed price per unit" },
  { value: "flat_fee", label: "Flat Fee", description: "Single flat rate" },
  { value: "tiered", label: "Tiered Pricing", description: "Volume-based pricing tiers" },
];

export default function PriceList() {
  const { 
    priceList, 
    fetchPriceList, 
    createPriceListItem, 
    updatePriceListItem,
    deletePriceListItem,
    dropdownOptions,
    fetchDropdownOptions,
    loading 
  } = useData();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    pricing_type: "per_item",
    base_price: 0,
    unit: "",
    waste_factor: 0,
    tier_pricing: [],
    enabled: true,
    margin: 0,
    default_visible: true,
    measurement_key: "",
    conversion_factor: 1,
    conversion_formula: ""
  });
  const [submitting, setSubmitting] = useState(false);

  // Compute conversion factor from formula string like "/ 50", "/ 100 * 3"
  const computeFactorFromFormula = (formula) => {
    if (!formula || !formula.trim()) return 1;
    try {
      // Evaluate "1 [formula]" safely — only allow numbers and basic math ops
      const clean = formula.trim();
      if (!/^[0-9.+\-*/\s()]+$/.test(clean)) return null;
      // eslint-disable-next-line no-new-func
      const result = new Function(`return 1 ${clean}`)();
      if (isNaN(result) || !isFinite(result)) return null;
      return result;
    } catch { return null; }
  };

  const handleFormulaChange = (formula) => {
    const factor = computeFactorFromFormula(formula);
    setFormData(prev => ({
      ...prev,
      conversion_formula: formula,
      conversion_factor: factor !== null ? factor : prev.conversion_factor
    }));
  };

  useEffect(() => {
    fetchPriceList();
    fetchDropdownOptions();
  }, [fetchPriceList, fetchDropdownOptions]);

  const serviceTypes = dropdownOptions.service_types || [];
  const costCategories = dropdownOptions.cost_categories || [];
  
  const categories = ["all", ...new Set(priceList.map(item => item.category).filter(Boolean))];

  const filteredItems = priceList.filter(item => {
    const matchesSearch = 
      item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === "all" || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingItem) {
        await updatePriceListItem(editingItem.id, formData);
        toast.success("Price item updated!");
      } else {
        await createPriceListItem(formData);
        toast.success("Price item created!");
      }
      setSubmitting(false);
      closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save price item");
      setSubmitting(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      ...item,
      conversion_formula: item.conversion_formula || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this price item?")) {
      try {
        await deletePriceListItem(id);
        toast.success("Moved to trash");
      } catch (error) {
        toast.error("Failed to delete price item");
      }
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      name: "",
      description: "",
      category: "",
      pricing_type: "per_item",
      base_price: 0,
      unit: "",
      waste_factor: 0,
      tier_pricing: [],
      enabled: true,
      margin: 0,
      default_visible: true,
      measurement_key: "",
      conversion_factor: 1
    });
  };

  const getCategoryLabel = (category) => {
    const cost = costCategories.find(c => c.value === category);
    if (cost) return cost.label;
    const service = serviceTypes.find(s => s.value === category);
    return service?.label || category;
  };

  return (
    <div className="min-h-screen">
      <Header title="Price List">
        <Button
          className="bg-[#0F4C81] hover:bg-[#0D4270] h-12 px-6"
          onClick={() => setShowModal(true)}
          data-testid="create-price-item-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Price Item
        </Button>
      </Header>

      <div className="p-4 sm:p-6">
          {/* Price List */}
          <div>
            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search price items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="search-price-list"
                />
              </div>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-full md:w-48 h-12">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>
                      {cat === "all" ? "All Categories" : getCategoryLabel(cat)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
              </div>
            ) : filteredItems.length > 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Item</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Category</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Pricing</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Base Price</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Margin</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Measurement</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item) => (
                      <tr 
                        key={item.id}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                        data-testid={`price-item-row-${item.id}`}
                      >
                        <td className="px-4 py-4">
                          <div>
                            <span className="font-medium text-slate-800">{item.name}</span>
                            {item.description && (
                              <p className="text-sm text-slate-500 truncate max-w-[200px]">{item.description}</p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <Badge variant="secondary" className="capitalize">
                            {getCategoryLabel(item.category)}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 text-slate-600 text-sm">
                          {PRICING_TYPES.find(p => p.value === item.pricing_type)?.label || item.pricing_type}
                        </td>
                        <td className="px-4 py-4">
                          <span className="font-medium text-emerald-600">
                            ${item.base_price?.toFixed(2)}/{item.unit || 'ea'}
                          </span>
                        </td>
                        <td className="px-4 py-4 text-slate-600 text-sm">
                          {item.margin > 0 ? `${item.margin}%` : '-'}
                        </td>
                        <td className="px-4 py-4 text-xs text-slate-500">
                          {item.measurement_key ? <Badge variant="outline" className="text-[10px]">{
                            {total_area: "Field SF", eaves_rakes: "E+R (waste)", eaves_rakes_raw: "E+R", hips_ridges: "H+R", ridges: "Ridges", valleys: "Valleys", hips: "Hips", eaves: "Eaves", rakes: "Rakes", boots: "Boots", drip_edge: "Drip Edge", flashing: "Flashing", osb: "OSB", transitions: "Trans.", sealant: "Sealant", spray: "Spray", delivery: "Delivery", labor_low: "Labor L", labor_mid: "Labor M", labor_high: "Labor H", field_squares: "Field Sq", total_squares: "Total Sq", starter_bundles: "Starter Bdl", hip_ridge_bundles: "H&R Bdl", drip_edge_pcs: "Drip Pcs"}[item.measurement_key] || item.measurement_key
                          }</Badge> : '-'}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(item.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-700 mb-2">
                  {searchQuery || filterCategory !== "all" ? "No items found" : "No price items yet"}
                </h3>
                <p className="text-slate-500 mb-4">Add pricing items to auto-populate quotes</p>
                <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={() => setShowModal(true)}>
                  <Plus className="w-5 h-5 mr-2" />Add Price Item
                </Button>
              </div>
            )}
          </div>
      </div>


      {/* Price Item Modal */}
      <Dialog open={showModal} onOpenChange={closeModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? "Edit Price Item" : "Add Price Item"}</DialogTitle>
            <DialogDescription>
              Configure pricing for this item
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Item Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Architectural Shingles"
                required
                className="h-12"
                data-testid="price-item-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Item description..."
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {costCategories.length > 0 ? costCategories.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>) : (
                      <>
                        <SelectItem value="Material">Material</SelectItem>
                        <SelectItem value="Labor">Labor</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Pricing Type</Label>
                <Select value={formData.pricing_type} onValueChange={(v) => setFormData({ ...formData, pricing_type: v })}>
                  <SelectTrigger className="h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICING_TYPES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Base Price ($)</Label>
                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span><Input
                  type="number"
                  step="0.01"
                  value={formData.base_price ?? ''}
                  onChange={(e) => setFormData({ ...formData, base_price: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  className="h-12 pl-7"
                /></div>
              </div>
              <div className="space-y-2">
                <Label>Margin (%)</Label>
                <div className="relative"><span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">%</span><Input
                  type="number"
                  step="0.1"
                  value={formData.margin ?? ''}
                  onChange={(e) => setFormData({ ...formData, margin: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  className="h-12 pr-8"
                  placeholder="0"
                /></div>
              </div>
              <div className="space-y-2">
                <Label>Waste Factor (%)</Label>
                <Input
                  type="number"
                  value={formData.waste_factor ?? ''}
                  onChange={(e) => setFormData({ ...formData, waste_factor: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                  className="h-12"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  placeholder="sq ft, lin ft, each"
                  className="h-12"
                />
              </div>
              {/* Auto-Calculate Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <p className="text-xs font-bold text-[#0F4C81] uppercase tracking-wider">Auto-Calculate from Measurement Report</p>
                <div className="space-y-2">
                  <Label className="text-sm">Measurement Field</Label>
                  <Select value={formData.measurement_key || "none"} onValueChange={(v) => setFormData({ ...formData, measurement_key: v === "none" ? "" : v })}>
                    <SelectTrigger className="h-12 bg-white">
                      <SelectValue placeholder="None (manual)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (manual entry)</SelectItem>
                      <SelectGroup>
                        <SelectLabel className="text-xs text-slate-400 px-2">Computed (auto-calculated)</SelectLabel>
                        <SelectItem value="field_squares">Field Squares (w/ waste)</SelectItem>
                        <SelectItem value="total_squares">Total Squares (field + starter + H&R)</SelectItem>
                        <SelectItem value="starter_bundles">Starter Bundles</SelectItem>
                        <SelectItem value="hip_ridge_bundles">H&R Bundles</SelectItem>
                        <SelectItem value="drip_edge_pcs">Drip Edge Pieces</SelectItem>
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel className="text-xs text-slate-400 px-2">Raw measurements</SelectLabel>
                        <SelectItem value="total_area">Field Area (SF)</SelectItem>
                        <SelectItem value="eaves">Eaves (LF)</SelectItem>
                        <SelectItem value="rakes">Rakes (LF)</SelectItem>
                        <SelectItem value="ridges">Ridges (LF)</SelectItem>
                        <SelectItem value="hips">Hips (LF)</SelectItem>
                        <SelectItem value="valleys">Valleys (LF)</SelectItem>
                        <SelectItem value="transitions">Transitions (LF)</SelectItem>
                        <SelectItem value="drip_edge">Drip Edge (LF)</SelectItem>
                        <SelectItem value="eaves_rakes">Eaves + Rakes (w/ waste)</SelectItem>
                        <SelectItem value="eaves_rakes_raw">Eaves + Rakes (no waste)</SelectItem>
                        <SelectItem value="hips_ridges">Hips + Ridges (LF)</SelectItem>
                        <SelectItem value="boots">Pipe Boots</SelectItem>
                        <SelectItem value="flashing">Flashing</SelectItem>
                        <SelectItem value="osb">OSB Sheets</SelectItem>
                        <SelectItem value="sealant">Sealant</SelectItem>
                        <SelectItem value="spray">Spray</SelectItem>
                        <SelectItem value="delivery">Delivery</SelectItem>
                        <SelectItem value="labor_low">Labor Low</SelectItem>
                        <SelectItem value="labor_mid">Labor Mid</SelectItem>
                        <SelectItem value="labor_high">Labor High</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    {formData.measurement_key === "eaves_rakes" ? "Sums Eaves + Rakes (with waste) from the measurement report" :
                     formData.measurement_key === "hips_ridges" ? "Sums Hips + Ridges from the measurement report" :
                     "Select a field to auto-fill quantity when a quote is linked to a measurement report"}
                  </p>
                </div>
                {formData.measurement_key && formData.measurement_key !== "none" && (
                  <div className="space-y-2">
                    <Label className="text-sm">Formula</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-500 whitespace-nowrap">measurement</span>
                      <Input
                        type="text"
                        value={formData.conversion_formula || ""}
                        onChange={(e) => handleFormulaChange(e.target.value)}
                        className="h-12 bg-white font-mono text-base"
                        placeholder="/ 50"
                        data-testid="conversion-formula-input"
                      />
                    </div>
                    {/* Live preview */}
                    {formData.conversion_formula && (() => {
                      const factor = computeFactorFromFormula(formData.conversion_formula);
                      if (factor === null) return <p className="text-xs text-red-500">Invalid formula — use basic math like / 50, / 100 * 3, * 0.5</p>;
                      const exampleVal = formData.measurement_key === "total_area" ? 2500 :
                        formData.measurement_key === "eaves" ? 120 :
                        formData.measurement_key === "eaves_rakes" ? 165 :
                        formData.measurement_key === "hips_ridges" ? 60 :
                        formData.measurement_key === "rakes" ? 45 :
                        formData.measurement_key === "ridges" ? 30 :
                        formData.measurement_key === "hips" ? 30 : 100;
                      const label = {total_area:"Field Area",eaves:"Eaves",eaves_rakes:"Eaves+Rakes",hips_ridges:"Hips+Ridges",ridges:"Ridges",hips:"Hips",rakes:"Rakes",boots:"Boots",drip_edge:"Drip Edge",valleys:"Valleys",flashing:"Flashing",osb:"OSB",transitions:"Transitions",sealant:"Sealant",field_squares:"Field Squares",total_squares:"Total Squares",starter_bundles:"Starter Bdl",hip_ridge_bundles:"H&R Bdl",drip_edge_pcs:"Drip Pcs",delivery:"Delivery",labor_low:"Labor L",labor_mid:"Labor M",labor_high:"Labor H"}[formData.measurement_key] || "Value";
                      const raw = exampleVal * factor;
                      const rounded = Math.ceil(raw);
                      return (
                        <div className="bg-white border border-blue-200 rounded-md px-3 py-2">
                          <p className="text-sm text-slate-700">
                            <span className="font-medium">{label} {exampleVal}</span>
                            <span className="text-slate-400"> {formData.conversion_formula} </span>
                            <span className="text-slate-500">= {raw.toFixed(2)}</span>
                            <span className="text-[#0F4C81] font-bold"> → {rounded} {formData.unit || "units"}</span>
                          </p>
                        </div>
                      );
                    })()}
                    {!formData.conversion_formula && (
                      <p className="text-xs text-slate-500">
                        {formData.measurement_key === "eaves"
                          ? "Example: IWS rolls at 50' usable → type / 50"
                          : formData.measurement_key === "eaves_rakes"
                          ? "Example: Starter bundles at 120 LF → type / 120"
                          : formData.measurement_key === "total_area"
                          ? "Example: Shingle bundles → type / 100 * 3"
                          : formData.measurement_key === "hips_ridges"
                          ? "Example: Ridge cap bundles at 20 LF → type / 20"
                          : "Type a formula like / 50, * 3, / 100 * 3"}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-700">Enabled</p>
                <p className="text-sm text-slate-500">Show in price list selection</p>
              </div>
              <Switch 
                checked={formData.enabled}
                onCheckedChange={(v) => setFormData({ ...formData, enabled: v })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1 h-12" onClick={closeModal}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 h-12 bg-[#0F4C81] hover:bg-[#0D4270]" disabled={submitting} data-testid="submit-price-item-btn">
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Saving...</>
                ) : (
                  editingItem ? "Update Item" : "Add Item"
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
